/**
 * Close Command Autocomplete Component
 * Shows list of all open tabs for @close command
 * Supports filtering by query text
 */

import { useEffect, useRef, useMemo, useCallback } from 'react';
import type { OpenedDocument } from '../contexts/AIContext';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

interface CloseCommandAutocompleteProps {
  documents: OpenedDocument[];
  query: string;
  onSelectTab: (tabId: number) => void;
  onCloseAll: () => void;
  onClose: () => void;
}

export default function CloseCommandAutocomplete({
  documents,
  query,
  onSelectTab,
  onCloseAll,
  onClose
}: CloseCommandAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter documents by query
  const filteredDocs = useMemo(() => {
    if (!query.trim()) return documents;
    const lowerQuery = query.toLowerCase();
    return documents.filter(doc => 
      doc.title.toLowerCase().includes(lowerQuery) ||
      doc.documentNumber?.toLowerCase().includes(lowerQuery) ||
      doc.type?.toLowerCase().includes(lowerQuery) ||
      doc.panelType?.toLowerCase().includes(lowerQuery) ||
      `tab ${doc.tabId}`.includes(lowerQuery)
    );
  }, [documents, query]);

  const isFiltering = query.trim().length > 0;
  // Only show "Close All" if not filtering or if query is "all"
  const showCloseAll = !isFiltering || query.toLowerCase() === 'all';

  const totalOptions = filteredDocs.length + (showCloseAll ? 1 : 0);

  const handleKeySelect = useCallback((index: number) => {
    if (showCloseAll && index === filteredDocs.length) {
      onCloseAll();
    } else if (filteredDocs[index]) {
      onSelectTab(filteredDocs[index].tabId);
    }
  }, [filteredDocs, showCloseAll, onSelectTab, onCloseAll]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: totalOptions,
    onSelect: handleKeySelect,
    onClose,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getTabInfo = (doc: OpenedDocument) => {
    // Handle page panels
    if (doc.tabType === 'page') {
      const pageIcons: Record<string, string> = {
        'reference-library': '📚',
        'projects': '📁',
        'company-docs': '🏢',
        'doc-control': '🗂️',
        'quality': '📊',
        'validation': '✓',
        'crosswalk': '🔀',
        'suppliers': '🏭',
      };
      return {
        icon: pageIcons[doc.panelType || ''] || '📄',
        label: 'Page',
        className: 'bg-info-light text-info'
      };
    }

    // Handle plan tabs
    if (doc.tabType === 'plan') {
      return {
        icon: '📋',
        label: 'Plan',
        className: 'bg-warning-light text-warning'
      };
    }

    // Handle document types
    switch (doc.type) {
      case 'project':
        return { icon: '📄', label: 'Project Doc', className: 'bg-info-light text-info' };
      case 'company':
        return { icon: '📋', label: 'Company Doc', className: 'bg-secondary text-secondary-foreground' };
      case 'standard':
        return { icon: '📘', label: 'Reference', className: 'bg-success-light text-success' };
      case 'prior-project':
        return { icon: '📦', label: 'Prior Project', className: 'bg-warning-light text-warning' };
      default:
        return { icon: '📄', label: 'Document', className: 'bg-muted text-muted-foreground' };
    }
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-highlight rounded px-0.5">{text.slice(index, index + query.length)}</span>
        {text.slice(index + query.length)}
      </>
    );
  };

  if (documents.length === 0) {
    return (
      <div
        ref={containerRef}
        className="command-autocomplete"
      >
        <div className="px-4 py-2 bg-destructive-light border-b border-destructive">
          <div className="text-xs font-semibold text-destructive">
            ✕ Close Tab
          </div>
        </div>
        <div className="px-4 py-8 text-center">
          <div className="text-2xl mb-2">📭</div>
          <div className="text-sm text-muted-foreground">
            No tabs open
          </div>
        </div>
        <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
          <span>Esc Close</span>
        </div>
      </div>
    );
  }

  const hasNoFilteredResults = filteredDocs.length === 0 && isFiltering;

  return (
    <div
      ref={containerRef}
      className="command-autocomplete"
    >
      <div className="px-4 py-2 bg-destructive-light border-b border-destructive">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-destructive">
            ✕ Close Tab
          </div>
          {isFiltering && (
            <div className="text-xs text-destructive">
              Filtering: "{query}"
            </div>
          )}
        </div>
      </div>

      {hasNoFilteredResults ? (
        <div className="px-4 py-8 text-center">
          <div className="text-2xl mb-2">🔍</div>
          <div className="text-sm text-muted-foreground">
            No tabs match "{query}"
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Type "all" to close all tabs
          </div>
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {filteredDocs.map((doc, index) => {
            const info = getTabInfo(doc);
            const isSelected = index === selectedIndex;

            return (
              <div
                key={doc.tabId}
                data-index={index}
                onClick={() => onSelectTab(doc.tabId)}
                className={`px-4 py-3 cursor-pointer border-b border-border transition-colors ${
                  isSelected
                    ? 'bg-destructive-light'
                    : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">{info.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Tab {doc.tabId}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${info.className}`}>
                        {info.label}
                      </span>
                    </div>
                    <div className="font-medium text-foreground truncate">
                      {highlightMatch(doc.title, query)}
                    </div>
                    {doc.documentNumber && doc.tabType !== 'page' && (
                      <div className="text-xs text-muted-foreground font-mono mt-1">
                        {highlightMatch(doc.documentNumber, query)}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-destructive opacity-50 hover:opacity-100">
                    ✕
                  </div>
                </div>
              </div>
            );
          })}

          {/* Close All Option */}
          {showCloseAll && (
            <div
              data-index={filteredDocs.length}
              onClick={() => onCloseAll()}
              className={`px-4 py-3 cursor-pointer border-t-2 border-destructive transition-colors ${
                selectedIndex === filteredDocs.length
                  ? 'bg-destructive-light'
                  : 'hover:bg-muted'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🗑️</span>
                <span className="font-semibold text-destructive">
                  Close All ({documents.length} tab{documents.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate • Enter Close • Esc Cancel</span>
          <span>
            {isFiltering 
              ? `${filteredDocs.length} of ${documents.length} tabs`
              : `${documents.length} open`
            }
          </span>
        </div>
      </div>
    </div>
  );
}
